import { callAPI } from './apiUtils';
import { apiUrls } from './apiUrls';

/**
 * Tap on Mobile (Worldline) payments the terminal has APPROVED but the POS has not yet recorded on
 * the server. The customer has been charged by then, so the approval must never be lost: it is
 * written to localStorage before the server is called and removed only when the server confirms
 * (or the order is already paid).
 *
 * The server's POST /v3/cart/payment is NOT idempotent for a repeated TapOnMobile payment (it
 * re-applies surcharges and loyalty points, re-prints and re-pushes to KDS), so after any failure
 * that may have been applied (timeout, network error, 5xx) the order's paid flag is checked before
 * the payment is posted again.
 */

export const PENDING_KEY = 'pending_tom_payments';
export const PENDING_EVENT = 'tomPendingChanged';
/** Automatic retries while the payment screen is open: after 2 s, 5 s, then 10 s. */
export const BACKOFF_MS = [2000, 5000, 10000];

let memoryFallback = [];

/**
 * One line into the native TOM debug report (the same channel as CheckoutSummary's nativeLog). Only
 * ids, the terminal reference, numbers and a truncated server message go through here: never tokens,
 * PINs, card data or the terminal response itself. Never throws.
 */
export const tomLog = (msg) => {
  try {
    window.AndroidPOS?.logWeb?.(String(msg));
  } catch (e) {
    /* logging must never affect a payment */
  }
};
const show = (v) => (v === null || v === undefined || v === '' ? 'null' : String(v));
const oneLine = (v, max = 200) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim().slice(0, max);
/** The terminal's own reference of a stored approval (record ids are `tom-<reference>`). */
export const refOfRecord = (rec) =>
  rec?.payload?.tom_response?.paymentSolutionReference || rec?.payload?.tom_response?.reference || String(rec?.id || '').replace(/^tom-/, '');

const emitChange = () => {
  try {
    window.dispatchEvent(new Event(PENDING_EVENT));
  } catch (e) {
    /* nothing listening */
  }
};

export const readPending = () => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((r) => r && typeof r === 'object' && r.id) : [];
  } catch (e) {
    return memoryFallback.slice();
  }
};

const writePending = (list) => {
  memoryFallback = list.slice();
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(list));
  } catch (e) {
    /* kept in memory for this session at least */
  }
  emitChange();
};

const patchRecord = (id, patch) => {
  writePending(readPending().map((r) => (r.id === id ? { ...r, ...patch } : r)));
};

export const removePending = (id) => writePending(readPending().filter((r) => r.id !== id));

export const findPending = (id) => readPending().find((r) => r.id === id) || null;

/** The terminal's own reference for this approval, so the same callback twice never makes two records. */
export const tomPaymentId = (tomResponse) => {
  const ref = tomResponse && (tomResponse.paymentSolutionReference || tomResponse.reference || tomResponse.transactionId);
  if (ref) return `tom-${String(ref)}`;
  return `tom-local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

/**
 * Stores an approved payment before anything is sent. `payload` is the exact POST body.
 * Returns the existing record (duplicate: true) when this approval is already stored.
 */
export const addPendingTomPayment = ({ orderId, tableId, orderNumber, amount, payload, tomResponse }) => {
  const id = tomPaymentId(tomResponse);
  const existing = findPending(id);
  if (existing) return { record: existing, duplicate: true };
  const record = {
    id,
    orderId: orderId || null,
    tableId: tableId || null,
    orderNumber: orderNumber || null,
    amount: Number.isFinite(Number(amount)) ? Number(amount) : null,
    payload,
    createdAt: Date.now(),
    attempts: 0,
    lastError: null,
    state: 'pending',
    // true once a POST may have reached the server without us seeing the answer
    ambiguous: false,
  };
  writePending([...readPending(), record]);
  return { record, duplicate: false };
};

/** true / false from the order record, or null when it could not be read. */
export const checkOrderPaid = async (orderId) => {
  if (!orderId) return null;
  try {
    const res = await callAPI(`${apiUrls.ORDER_DETAILS_URL}?id=${encodeURIComponent(orderId)}`, {}, 'GET', null, {}, {}, false);
    const paid = res?.data?.data?.paid;
    if (paid === undefined || paid === null) return null;
    return paid === true || paid === 1 || paid === '1';
  } catch (e) {
    return null;
  }
};

const inflight = new Set();
const isDefinitiveRejection = (status) => (status >= 400 && status < 500) || status === 501;

/**
 * Sends one stored payment to the server, safely.
 *
 * outcome:
 *   'recorded'     server answered status 1 (record removed; `response` is the answer)
 *   'already_paid' the order was already paid, nothing was posted (record removed)
 *   'rejected'     the server answered and refused (message shown); nothing was applied
 *   'ambiguous'    the request may or may not have been applied (network error, timeout, 5xx)
 *   'unverified'   a previous attempt may have been applied and the order could not be checked now; nothing was posted
 *   'busy' | 'missing'
 *
 * `manual` lets a person re-post when the order's paid flag cannot be read only because its id is
 * unknown (never because of the network).
 */
export const submitPendingTomPayment = async (id, { manual = false } = {}) => {
  if (inflight.has(id)) return { outcome: 'busy' };
  const rec = findPending(id);
  if (!rec) return { outcome: 'missing' };
  inflight.add(id);
  try {
    if (rec.ambiguous) {
      const paid = await checkOrderPaid(rec.orderId);
      tomLog(`TOM_PAID_CHECK paid=${paid === null ? 'error' : paid}`);
      if (paid === true) {
        removePending(id);
        tomLog(`TOM_PENDING cleared id=${refOfRecord(rec)}`);
        return { outcome: 'already_paid' };
      }
      if (paid === null && !(manual && !rec.orderId)) {
        const message = 'Could not confirm whether the order was already recorded. Check the connection and try again.';
        patchRecord(id, { lastError: message });
        return { outcome: 'unverified', message };
      }
    }

    // From here a POST may reach the server whatever we see back, until it answers definitively.
    patchRecord(id, { attempts: (rec.attempts || 0) + 1, ambiguous: true, lastAttemptAt: Date.now() });
    tomLog(`TOM_BACKEND_POST attempt=${(rec.attempts || 0) + 1} order_id=${show(rec.payload?.order_id)} table_id=${show(rec.payload?.table_id)}`);
    let res;
    try {
      res = await callAPI(apiUrls.ORDER_PAYMENT, {}, 'POST', rec.payload);
    } catch (err) {
      const status = err?.response?.status;
      const serverMessage = err?.response?.data?.message;
      const message =
        (typeof serverMessage === 'string' && serverMessage.trim()) ||
        (status ? `The server answered with an error (${status}).` : 'No connection to the server.');
      const definitive = isDefinitiveRejection(status);
      tomLog(`TOM_BACKEND_RESULT http=${status || 'network-error'} status=${show(err?.response?.data?.status)} message=${oneLine(message)}`);
      patchRecord(id, { lastError: message, ambiguous: !definitive });
      return { outcome: definitive ? 'rejected' : 'ambiguous', message };
    }
    tomLog(`TOM_BACKEND_RESULT http=${res?.status ?? 'n/a'} status=${show(res?.data?.status)} message=${oneLine(res?.data?.message)}`);
    if (Number(res?.data?.status) === 1) {
      removePending(id);
      tomLog(`TOM_PENDING cleared id=${refOfRecord(rec)}`);
      return { outcome: 'recorded', response: res };
    }
    const message = res?.data?.message || 'The server did not accept the payment.';
    patchRecord(id, { lastError: message, ambiguous: false });
    return { outcome: 'rejected', message, response: res };
  } finally {
    inflight.delete(id);
  }
};

export const formatAmount = (amount) => (Number.isFinite(Number(amount)) ? `$${Number(amount).toFixed(2)}` : '');
